import { ErrorMessage, Field } from "formik";
import TextError from "../../forms/TextError";
import { Copy, Trash2, CheckCircle, AlertCircle, Zap } from "lucide-react";
import { memo, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import ItenerayPopup from "./ItenerayPopup";
import ChargeLineItemsEditor from "./ChargeLineItemsEditor";
import { money, optionGrandTotal } from "./chargeUtils";
import { handleKeyPress } from "../../functions/Utils";

const SCHEDULE_GRID = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 110px 1fr",
  gap: 10,
};

const INPUT_CLS =
  "h-[36px] px-3 focus:outline-none block w-full text-sm border border-gray-200 rounded-lg text-gray-800 bg-white focus:border-yellow-400";
const LABEL_CLS =
  "block text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1";

// Dev-only convenience: typing this exact value into Transit reveals an
// "Autofill" button that fills the rest of the form with sample data, so a
// developer testing the flow repeatedly doesn't have to hand-fill every
// field each time. Enabled for local dev builds (NODE_ENV !== "production")
// and on the "dev." deployment host — every deployed build is a production
// build regardless of server, so NODE_ENV alone can't tell dev.send-asap.ai
// apart from the real production domain. Never enabled anywhere else.
const DEV_AUTOFILL_TRIGGER = "765472";
const IS_DEV_HOST = typeof window !== "undefined" && /^dev\./i.test(window.location.hostname);
const DEV_AUTOFILL_ENABLED = process.env.NODE_ENV !== "production" || IS_DEV_HOST;

const checkItineraryComplete = (option) =>
  option?.itinerary?.length > 0 &&
  option.itinerary.every(
    ({ carrier, origin, destination, departure, arrival, aircraft, flight }) =>
      carrier && origin && destination && departure && arrival && aircraft && flight
  );

const routeSummary = (option) => {
  const segs = option?.itinerary || [];
  if (!segs.length) return "";
  const first = segs[0]?.origin;
  const last = segs[segs.length - 1]?.destination;
  if (!first || !last) return "";
  return segs.length > 1 ? `${first} → ${last} · ${segs.length} legs` : `${first} → ${last}`;
};

const FlightOptionCard = memo(
  ({
    formikData,
    basePath,
    option,
    optionIndex,
    carrierCode,
    chargeList,
    totalWeight,
    airLineList,
    airPort,
    originAirport,
    destAirport,
    canRemove,
    onDuplicate,
    onRemove,
    allOptionRefs,
  }) => {
    const [itineraryOpen, setItineraryOpen] = useState(false);
    const itineraryComplete = checkItineraryComplete(option);
    const segmentCount = option?.itinerary?.length || 0;
    const grandTotal = optionGrandTotal(option, totalWeight);
    const route = routeSummary(option);

    // Every other flight option in the quote (any airline) — lets each
    // section pull its data from an already-entered option instead of
    // re-typing it.
    const copySources = (allOptionRefs || []).filter(
      (ref) => !(ref.airlineIndex === basePath.airlineIndex && ref.optionIndex === optionIndex)
    );

    const isDevAutofillTrigger =
      DEV_AUTOFILL_ENABLED && String(option?.transitDuration) === DEV_AUTOFILL_TRIGGER;

    const handleDevAutofill = (e) => {
      e.preventDefault();
      const fallbackAirline = airLineList?.[0];
      const chosenCode = carrierCode || fallbackAirline?.code || "";
      const chosenName =
        (airLineList || []).find((a) => a.code === chosenCode)?.name || fallbackAirline?.name || "";
      const departure = dayjs().add(2, "day").hour(10).minute(0).second(0);
      const arrival = dayjs().add(3, "day").hour(14).minute(0).second(0);
      const chargeA = chargeList?.[0]?.value || "";
      const chargeB = chargeList?.[1]?.value || chargeA;
      const chargeC = chargeList?.[2]?.value || chargeA;

      if (!formikData.values.quoteExpiry) {
        formikData.setFieldValue("quoteExpiry", dayjs().add(30, "day"));
      }
      formikData.setFieldValue(`airlines.${basePath.airlineIndex}.carrierCode`, chosenCode);
      formikData.setFieldValue(`airlines.${basePath.airlineIndex}.carrierName`, chosenName);

      formikData.setFieldValue(`${basePath.path}.departureDateAndTime`, departure);
      formikData.setFieldValue(`${basePath.path}.arrivalDateAndTime`, arrival);
      formikData.setFieldValue(`${basePath.path}.transitDuration`, arrival.diff(departure, "minutes"));
      formikData.setFieldValue(`${basePath.path}.serviceType`, "Express");

      formikData.setFieldValue(`${basePath.path}.charges.origin.items`, [
        { charge: chargeA, unit: "perkg", unitRate: 45 },
      ]);
      formikData.setFieldValue(`${basePath.path}.charges.destination.items`, [
        { charge: chargeB, unit: "lumpsum", unitRate: 2500 },
      ]);
      formikData.setFieldValue(`${basePath.path}.charges.freightAndSurcharges.items`, [
        { charge: chargeC, unit: "perkg", unitRate: 120 },
      ]);

      formikData.setFieldValue(`${basePath.path}.itinerary`, [
        {
          carrier: chosenCode,
          operationType: "FRT",
          origin: originAirport,
          destination: destAirport,
          departure,
          arrival,
          aircraft: "B787-9",
          flight: "AI101",
        },
      ]);
    };

    return (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(16,24,40,0.04), 0 1px 2px rgba(16,24,40,0.03)",
        }}
      >
        {/* Option header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            padding: "12px 16px",
            borderBottom: "1px solid #f0dea3",
            background: "linear-gradient(135deg, #fffbf0 0%, #fff8e1 100%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#FCB614",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {optionIndex + 1}
            </span>
            <span style={{ fontWeight: 600, color: "#374151", fontSize: 13 }}>
              Flight option {optionIndex + 1}
            </span>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setItineraryOpen(true);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                fontWeight: 500,
                padding: "3px 10px",
                borderRadius: 999,
                border: itineraryComplete ? "1px solid #86efac" : "1px solid #d1d5db",
                background: itineraryComplete ? "#f0fdf4" : "#fff",
                color: itineraryComplete ? "#16a34a" : "#6b7280",
                cursor: "pointer",
                transition: "background 0.15s ease, border-color 0.15s ease",
              }}
            >
              {itineraryComplete ? (
                <>
                  <CheckCircle size={12} />
                  Itinerary · {segmentCount} seg
                </>
              ) : (
                <>
                  <AlertCircle size={12} />
                  Itinerary
                </>
              )}
            </button>

            {route && <span style={{ fontSize: 12, color: "#9ca3af" }}>{route}</span>}

            {formikData?.errors?.airlines?.[basePath.airlineIndex]?.flightOptions?.[optionIndex]
              ?.itineraryField && (
              <span style={{ color: "#ef4444", fontSize: 11 }}>
                {
                  formikData.errors.airlines[basePath.airlineIndex].flightOptions[optionIndex]
                    .itineraryField
                }
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: 12,
                color: "#8c6a12",
                background: "rgba(255,255,255,0.6)",
                border: "1px solid #f2e2b8",
                borderRadius: 999,
                padding: "4px 12px",
              }}
            >
              Grand total <strong style={{ color: "#1f2937" }}>{money(grandTotal)}</strong>
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  onDuplicate();
                }}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "#f3f4f6",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9ca3af",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#ebedf0")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                title="Duplicate option"
              >
                <Copy size={12} />
              </button>
              {canRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: "#fef2f2",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fca5a5",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fef2f2")}
                  title="Remove option"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "14px 16px", background: "#fff", display: "flex", flexDirection: "column", gap: 12 }}>
          {isDevAutofillTrigger && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px dashed #FCB614",
                background: "#fffbf0",
              }}
            >
              <span style={{ fontSize: 12.5, color: "#8c6a12", fontWeight: 500 }}>
                Dev shortcut detected — fill carrier, schedule, charges and itinerary with sample data?
              </span>
              <button
                type="button"
                onClick={handleDevAutofill}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: "#1f2937",
                  background: "#FCB614",
                  border: "none",
                  borderRadius: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(252,182,20,0.35)",
                }}
              >
                <Zap size={13} />
                Autofill
              </button>
            </div>
          )}

          {/* Schedule row */}
          <div style={SCHEDULE_GRID}>
            <div>
              <label className={LABEL_CLS}>Departure</label>
              <Field
                id={`${basePath.path}.departureDateAndTime`}
                name={`${basePath.path}.departureDateAndTime`}
              >
                {() => (
                  <DatePicker
                    value={option.departureDateAndTime != null ? dayjs(option.departureDateAndTime) : null}
                    showTime={{ format: "HH:mm", showSecond: false }}
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={(current) =>
                      current &&
                      (current < dayjs().startOf("day") || current > dayjs().add(3, "month").endOf("day"))
                    }
                    placeholder="Date"
                    style={{ width: "100%", height: 36 }}
                    className="text-sm rounded-lg"
                    onChange={(date) =>
                      formikData.setFieldValue(`${basePath.path}.departureDateAndTime`, date)
                    }
                  />
                )}
              </Field>
              <ErrorMessage name={`${basePath.path}.departureDateAndTime`} component={TextError} />
            </div>

            <div>
              <label className={LABEL_CLS}>Arrival</label>
              <Field id={`${basePath.path}.arrivalDateAndTime`} name={`${basePath.path}.arrivalDateAndTime`}>
                {() => (
                  <DatePicker
                    showTime={{ format: "HH:mm", showSecond: false }}
                    format="YYYY-MM-DD HH:mm"
                    value={option.arrivalDateAndTime != null ? dayjs(option.arrivalDateAndTime) : null}
                    disabledDate={(current) => {
                      const dep = dayjs(option.departureDateAndTime);
                      return (
                        current &&
                        (current.isBefore(dep.add(1, "minute")) ||
                          current > dayjs().add(3, "month").endOf("day"))
                      );
                    }}
                    placeholder="Date"
                    style={{ width: "100%", height: 36 }}
                    className="text-sm rounded-lg"
                    onChange={(date) => {
                      formikData.setFieldValue(`${basePath.path}.arrivalDateAndTime`, date);
                      formikData.setFieldValue(
                        `${basePath.path}.transitDuration`,
                        dayjs(date).diff(dayjs(option.departureDateAndTime), "minutes")
                      );
                    }}
                  />
                )}
              </Field>
              <ErrorMessage name={`${basePath.path}.arrivalDateAndTime`} component={TextError} />
            </div>

            <div>
              <label className={LABEL_CLS}>Transit</label>
              <Field
                type="text"
                id={`${basePath.path}.transitDuration`}
                name={`${basePath.path}.transitDuration`}
                placeholder="min"
                className={INPUT_CLS}
                onChange={formikData.handleChange}
                onKeyPress={handleKeyPress}
              />
            </div>

            <div>
              <label className={LABEL_CLS}>Service type</label>
              <Field
                type="text"
                id={`${basePath.path}.serviceType`}
                name={`${basePath.path}.serviceType`}
                placeholder="Express, Standard..."
                className={INPUT_CLS}
                onChange={formikData.handleChange}
              />
              <ErrorMessage name={`${basePath.path}.serviceType`} component={TextError} />
            </div>
          </div>

          {/* Charges */}
          <ChargeLineItemsEditor
            formikData={formikData}
            basePath={`${basePath.path}.charges.origin`}
            sectionKey="origin"
            items={option?.charges?.origin?.items}
            chargeList={chargeList}
            title="Origin charges"
            totalWeight={totalWeight}
            copySources={copySources}
          />
          <ChargeLineItemsEditor
            formikData={formikData}
            basePath={`${basePath.path}.charges.destination`}
            sectionKey="destination"
            items={option?.charges?.destination?.items}
            chargeList={chargeList}
            title="Destination charges"
            totalWeight={totalWeight}
            copySources={copySources}
          />
          <ChargeLineItemsEditor
            formikData={formikData}
            basePath={`${basePath.path}.charges.freightAndSurcharges`}
            sectionKey="freightAndSurcharges"
            items={option?.charges?.freightAndSurcharges?.items}
            chargeList={chargeList}
            title="Freight & surcharges"
            totalWeight={totalWeight}
            copySources={copySources}
          />
        </div>

        {itineraryOpen && (
          <ItenerayPopup
            formikData={formikData}
            anchorEl={itineraryOpen}
            handleClose={() => setItineraryOpen(false)}
            basePath={basePath.path}
            option={option}
            carrierCode={carrierCode}
            copySources={copySources}
            airLineList={airLineList}
            airPort={airPort}
            originAirport={originAirport}
            destAirport={destAirport}
          />
        )}
      </div>
    );
  }
);

export default FlightOptionCard;
